import { ApiResponse } from "../interfaces/apiResponse.interface";
import { DocumentGroup, Tester, TesterVehicle, VehicleDocumentsViewModel, Document, VehicleIncidentsViewModel, TesterContact, LookUp, StateLookUp, VehicleTypeLookUp, DocumentTypeLookUp } from "../interfaces/tester.interface";
import { APIMAPIDeleteRequest, APIMAPIGetRequest, APIMAPIPostRequest, APIMAPIPutRequest } from "./APIMRequest";
import { AttachmentDeleteRequest } from "./AttachmentDeleteRequest";
import { AttachmentGetRequest } from "./AttachmentGetRequest";
import { AttachmentPutRequest } from "./AttachmentPutRequest";
import { ErrorLogger, ErrorSeverity } from "./errorLogger.service";

interface TesterVehicleCount {
    testerId: number;
    automatedVehiclesCount: number;
    pddsCount: number;
}

export class TesterService {

    async GetAllTesters(): Promise<ApiResponse<Array<Tester>>> {
        try {
            let apimGetRequest = new APIMAPIGetRequest("api/v1/testers");
            let res = (await apimGetRequest.send()).result;

            return {
                result: res, statusMessage: "", messageCode: 200, statusCode: "Y"
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch testers";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetAllTesters',
                severity: ErrorSeverity.Error
            });
            return {
                result: [] as Array<Tester>,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetTesterById(testerId: number): Promise<ApiResponse<Tester | null>> {
        try {
            let apimGetRequest = new APIMAPIGetRequest("api/v1/testers/" + testerId);
            let res = (await apimGetRequest.send()).result;

            return {
                result: res, statusMessage: "", messageCode: 200, statusCode: "Y"
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch tester";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetTesterById',
                severity: ErrorSeverity.Error,
                additionalData: { testerId }
            });
            return {
                result: null,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async SaveTester(tester: Tester): Promise<ApiResponse<Tester>> {
        try {
            if (!tester.testerContacts || tester.testerContacts.length === 0) {
                tester.testerContacts = [] as TesterContact[];
                tester.testerContacts.push({} as TesterContact);
            }

            tester.testerContacts[0].pocName = tester.pocName;
            tester.testerContacts[0].pocTitle = tester.pocTitle;
            tester.testerContacts[0].pocPhone = tester.pocPhone.replaceAll(/\D/g, '')
            tester.testerContacts[0].pocEmail = tester.pocEmail;

            tester.testerContacts[0].createdBy = "";
            tester.testerContacts[0].createdDt = new Date();
            tester.testerContacts[0].updatedBy = "";
            tester.testerContacts[0].updatedDt = new Date();

            tester.physicalAddrLine2 = tester.physicalAddrLine2 || "";
            tester.mailingAddrLine1 = tester.mailingAddrLine1 || "";
            tester.mailingAddrLine2 = tester.mailingAddrLine2 || "";
            tester.mailingAddrCity = tester.mailingAddrCity || "";
            tester.mailingAddrstateCD = tester.mailingAddrstateCD || "";
            tester.mailingAddrZip = tester.mailingAddrZip || "";
            tester.comment = tester.comment || "";

            let apimRequest: any;
            if (tester.testerId === 0)
                apimRequest = new APIMAPIPostRequest("api/v1/testers", JSON.stringify(tester, function (k, v) { return v === undefined ? null : v; }));
            else
                apimRequest = new APIMAPIPutRequest("api/v1/testers", JSON.stringify(tester, function (k, v) { return v === undefined ? null : v; }));

            let res = (await apimRequest.send()).result;

            return {
                result: res, statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to save tester";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'SaveTester',
                severity: ErrorSeverity.Error,
                additionalData: { testerId: tester.testerId, testerName: tester.testerName }
            });
            return {
                result: {} as Tester,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetVehiclesByTesterId(testerId: number): Promise<ApiResponse<Array<TesterVehicle> | null>> {
        try {
            let apimGetRequest = new APIMAPIGetRequest("api/v1/testers/" + testerId + "/vehicles");
            let vehicles = (await apimGetRequest.send()).result as Array<TesterVehicle>;

            vehicles = this.formatGetVehiclesByTesterId(vehicles);

            return {
                result: vehicles, statusMessage: "", messageCode: 200, statusCode: "Y"
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch vehicles";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetVehiclesByTesterId',
                severity: ErrorSeverity.Error,
                additionalData: { testerId }
            });
            return {
                result: null,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async SaveVehicle(vehicle: TesterVehicle): Promise<ApiResponse<TesterVehicle>> {
        try {
            vehicle.licensePlateNumber = vehicle.licensePlateNumber || "";

            let apimRequest: any;
            if (vehicle.vehicleId === 0)
                apimRequest = new APIMAPIPostRequest("api/v1/vehicles", JSON.stringify(vehicle, function (k, v) {
                    if (k === 'vehicleId' && v === 0) return null;
                    return v === undefined ? null : v;
                }));
            else
                apimRequest = new APIMAPIPutRequest("api/v1/vehicles", JSON.stringify(vehicle, function (k, v) { return v === undefined ? null : v; }));

            let res = (await apimRequest.send()).result;

            return {
                result: res, statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to save vehicle";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'SaveVehicle',
                severity: ErrorSeverity.Error,
                additionalData: { vehicleId: vehicle.vehicleId }
            });
            return {
                result: {} as TesterVehicle,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async DeleteVehicle(vehicleId: number): Promise<ApiResponse<boolean>> {
        try {
            let apimDeleteRequest = new APIMAPIDeleteRequest("api/v1/vehicles/" + vehicleId);
            await apimDeleteRequest.send();

            return {
                result: true, statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to delete vehicle";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'DeleteVehicle',
                severity: ErrorSeverity.Error,
                additionalData: { vehicleId }
            });
            return {
                result: false,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetDocumentById(documentId: number): Promise<ApiResponse<Document>> {
        try {
            let apimGetRequest = new APIMAPIGetRequest("api/v1/documents/" + documentId);
            let res = (await apimGetRequest.send()).result;

            return {
                result: res,
                statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch document";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetDocumentById',
                severity: ErrorSeverity.Error,
                additionalData: { documentId }
            });
            return {
                result: {} as Document,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetDocumentGroupById(documentGroupId: number): Promise<ApiResponse<DocumentGroup>> {
        try {
            let apimGetRequest = new APIMAPIGetRequest("api/v1/documents?documentGroupId=" + documentGroupId);
            let res = (await apimGetRequest.send()).result;

            let documentGroup = { documentGroupId: documentGroupId, documents: res } as DocumentGroup;

            return {
                result: documentGroup,
                statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch documents";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetDocumentGroupById',
                severity: ErrorSeverity.Error,
                additionalData: { documentGroupId }
            });
            return {
                result: {} as DocumentGroup,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetDocumentsByVehicleId(documentGroupId: number): Promise<ApiResponse<VehicleDocumentsViewModel>> {
        try {
            let documentGroup = (await this.GetDocumentGroupById(documentGroupId)).result;

            if (!documentGroup) {
                return {
                    result: {
                        vehicleId: 0,
                        RegistrationDocument: {} as Document,
                        VehicleDocumentGroup: {} as DocumentGroup
                    } as VehicleDocumentsViewModel,
                    statusMessage: "Failed to fetch vehicle documents",
                    messageCode: 500,
                    statusCode: "N"
                };
            }

            return {
                result: {
                    vehicleId: 0,
                    RegistrationDocument: {} as Document,
                    VehicleDocumentGroup: documentGroup
                },
                statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch vehicle documents";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetDocumentsByVehicleId',
                severity: ErrorSeverity.Error,
                additionalData: { documentGroupId }
            });
            return {
                result: {
                    vehicleId: 0,
                    RegistrationDocument: {} as Document,
                    VehicleDocumentGroup: {} as DocumentGroup
                } as VehicleDocumentsViewModel,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetDocumentGroupsByTesterId(testerId: number): Promise<ApiResponse<Array<DocumentGroup>>> {
        try {
            let apimGetRequest = new APIMAPIGetRequest("api/v1/documentgroups?testerId=" + testerId);
            let res = (await apimGetRequest.send()).result;

            return {
                result: res,
                statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch document groups";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetDocumentGroupsByTesterId',
                severity: ErrorSeverity.Error,
                additionalData: { testerId }
            });
            return {
                result: [] as Array<DocumentGroup>,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async SaveVehicleDocuments(vehicleDocuments: VehicleDocumentsViewModel): Promise<ApiResponse<VehicleDocumentsViewModel>> {
        try {
            const regDocRes = await this.SaveDocument(vehicleDocuments.RegistrationDocument!);
            if (regDocRes.statusCode === "N") {
                return {
                    result: {
                        vehicleId: 0,
                        RegistrationDocument: {} as Document,
                        VehicleDocumentGroup: {} as DocumentGroup
                    } as VehicleDocumentsViewModel,
                    statusMessage: regDocRes.statusMessage,
                    messageCode: regDocRes.messageCode,
                    statusCode: "N"
                };
            }
            vehicleDocuments.RegistrationDocument!.documentId = regDocRes.result.documentId;

            if ((vehicleDocuments.VehicleDocumentGroup?.documents?.length ?? 0) > 0) {
                const groupRes = await this.SaveDocumentGroup(vehicleDocuments.VehicleDocumentGroup!);
                if (groupRes.statusCode === "N") {
                    return {
                        result: {
                            vehicleId: 0,
                            RegistrationDocument: {} as Document,
                            VehicleDocumentGroup: {} as DocumentGroup
                        } as VehicleDocumentsViewModel,
                        statusMessage: groupRes.statusMessage,
                        messageCode: groupRes.messageCode,
                        statusCode: "N"
                    };
                }
                vehicleDocuments.VehicleDocumentGroup!.documentGroupId = groupRes.result.documentGroupId;
            }
            else {
                vehicleDocuments.VehicleDocumentGroup!.documentGroupId = 0;
            }

            return {
                result: vehicleDocuments, statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to save vehicle documents";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'SaveVehicleDocuments',
                severity: ErrorSeverity.Error
            });
            return {
                result: {
                    vehicleId: 0,
                    RegistrationDocument: {} as Document,
                    VehicleDocumentGroup: {} as DocumentGroup
                } as VehicleDocumentsViewModel,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async SaveDocument(document: Document): Promise<ApiResponse<Document>> {
        try {
            let apimRequest: any;

            if ((document.documentId ?? 0) <= 0) {
                document.documentId = 0;
                apimRequest = new APIMAPIPostRequest("api/v1/documents", JSON.stringify(document, function (k, v) {
                    if (k === 'documentId' && v === 0) return null;
                    return v === undefined ? null : v;
                }));
            }
            else
                apimRequest = new APIMAPIPutRequest("api/v1/documents", JSON.stringify(document, function (k, v) { return v === undefined ? null : v; }));

            let res = (await apimRequest.send()).result;

            return {
                result: res, statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to save document";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'SaveDocument',
                severity: ErrorSeverity.Error,
                additionalData: { documentId: document.documentId }
            });
            return {
                result: {} as Document,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async SaveDocumentGroup(documentGroup: DocumentGroup): Promise<ApiResponse<DocumentGroup>> {
        try {
            let apimRequest: any;

            if (documentGroup.IsNewGroup)
                apimRequest = new APIMAPIPostRequest("api/v1/documentgroups", JSON.stringify(documentGroup, function (k, v) {
                    if ((k === 'documentGroupId' || k === 'documentId') && v === 0) return null;
                    return v === undefined ? null : v;
                }));
            else
                apimRequest = new APIMAPIPutRequest("api/v1/documentgroups", JSON.stringify(documentGroup, function (k, v) { return v === undefined ? null : v; }));

            let res = (await apimRequest.send()).result;

            return {
                result: res, statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to save document group";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'SaveDocumentGroup',
                severity: ErrorSeverity.Error,
                additionalData: { documentGroupId: documentGroup.documentGroupId }
            });
            return {
                result: {} as DocumentGroup,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async DeleteDocument(document: Document): Promise<ApiResponse<Document>> {
        try {
            await this.DeleteFile(document);

            let apimDeleteRequest = new APIMAPIDeleteRequest("api/v1/documents/" + document.documentId);
            let res = (await apimDeleteRequest.send()).result;

            return {
                result: res, statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to delete document";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'DeleteDocument',
                severity: ErrorSeverity.Error,
                additionalData: { documentId: document.documentId }
            });
            return {
                result: {} as Document,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetIncidentByIncidentId(incidentId: string): Promise<ApiResponse<VehicleIncidentsViewModel>> {
        try {
            let apimGetRequest = new APIMAPIGetRequest("api/v1/incident/" + incidentId);
            let res = (await apimGetRequest.send()).result;

            return {
                result: res as VehicleIncidentsViewModel,
                statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch incident";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetIncidentByIncidentId',
                severity: ErrorSeverity.Error,
                additionalData: { incidentId }
            });
            return {
                result: {} as VehicleIncidentsViewModel,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetIncidentsByVehicleId(vehicleId: number): Promise<ApiResponse<Array<VehicleIncidentsViewModel>>> {
        try {
            let apimGetRequest = new APIMAPIGetRequest("api/v1/incidents/vehicle/" + vehicleId);
            let res = (await apimGetRequest.send()).result;

            return {
                result: res as VehicleIncidentsViewModel[],
                statusMessage: "", statusCode: "Y", messageCode: 200
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch incidents";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetIncidentsByVehicleId',
                severity: ErrorSeverity.Error,
                additionalData: { vehicleId }
            });
            return {
                result: [] as Array<VehicleIncidentsViewModel>,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async UploadFile(file: File): Promise<string> {
        const fileReq = new AttachmentPutRequest(file);
        const fileResp = await fileReq.send();
        if (fileResp.statusCode !== "Y") {
            throw new Error(fileResp.statusMessage);
        }
        return fileResp.result;
    }

    async DownloadFile(document: Document): Promise<boolean> {
        const request = new AttachmentGetRequest(document);
        const blob = await request.send();

        if (!(blob instanceof Blob)) {
            console.error("Download failed: response is not a Blob");
            ErrorLogger.get().logError(new Error('Download failed: response is not a Blob'), {
                component: 'TesterService',
                action: 'DownloadFile',
                severity: ErrorSeverity.Error,
                additionalData: {
                    documentTitle: document.documentTitle,
                    documentLocation: document.documentLocation
                }
            });
            return false;
        }

        const mime = blob.type || 'application/pdf';
        const file = document.documentTitle;

        const a = globalThis.document.createElement('a');

        a.download = file ?? "";
        a.href = globalThis.URL.createObjectURL(blob);
        a.dataset.downloadurl = [mime, a.download, a.href].join(':');

        a.click();

        return true;
    }


    async DeleteFile(document: Document): Promise<boolean> {
        const request = new AttachmentDeleteRequest(document);
        await request.send();
        return true;
    };

    async GetAllLookUps(): Promise<ApiResponse<Array<LookUp>>> {
        try {
            if (!sessionStorage.getItem("LOOKUP")) {
                let apimGetRequest = new APIMAPIGetRequest("api/v1/lookupData");
                let res = (await apimGetRequest.send()).result;
                sessionStorage.setItem("LOOKUP", JSON.stringify(res));
            }

            return {
                result: JSON.parse(sessionStorage.getItem("LOOKUP") as string), statusMessage: "", messageCode: 200, statusCode: "Y"
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch lookups";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetAllLookUps',
                severity: ErrorSeverity.Error
            });
            return {
                result: [] as Array<LookUp>,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetStateLookUp(): Promise<ApiResponse<Array<StateLookUp>>> {
        try {
            if (!sessionStorage.getItem("STATELOOKUP")) {
                let res = (await this.GetAllLookUps()).result;

                if (!res) {
                    return {
                        result: [] as Array<StateLookUp>,
                        statusMessage: "Failed to load state lookups",
                        messageCode: 500,
                        statusCode: "N"
                    };
                }

                let _states = res.find(x => x.key === "STATE")?.value;

                let states = [] as StateLookUp[];
                if (_states !== null && _states !== undefined) {
                    for (const state of _states) {
                        if (state.isActive === "Y")
                            states.push({ StateCode: state.code, StateName: state.description } as StateLookUp);
                    }
                    sessionStorage.setItem("STATELOOKUP", JSON.stringify(states));
                }
            }

            return {
                result: JSON.parse(sessionStorage.getItem("STATELOOKUP") as string),
                statusMessage: "",
                messageCode: 200,
                statusCode: "Y"
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch state lookups";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetStateLookUp',
                severity: ErrorSeverity.Error
            });
            return {
                result: [] as Array<StateLookUp>,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetVehicleTypeLookUp(): Promise<ApiResponse<Array<VehicleTypeLookUp>>> {
        try {
            if (!sessionStorage.getItem("VEHICLETYPELOOKUP")) {
                let res = (await this.GetAllLookUps()).result;

                if (!res) {
                    return {
                        result: [] as Array<VehicleTypeLookUp>,
                        statusMessage: "Failed to load vehicle type lookups",
                        messageCode: 500,
                        statusCode: "N"
                    };
                }

                let _vehicletypes = res.find(x => x.key === "VEHICLE_TYPE")?.value;

                let vehicletypes = [] as VehicleTypeLookUp[];

                if (_vehicletypes) {
                    for (const vehicleType of _vehicletypes) {
                        if (vehicleType.isActive === "Y")
                            vehicletypes.push({ VehicleTypeCode: vehicleType.code, VehicleTypeName: vehicleType.description } as VehicleTypeLookUp);
                    }
                }

                sessionStorage.setItem("VEHICLETYPELOOKUP", JSON.stringify(vehicletypes));
            }

            return {
                result: JSON.parse(sessionStorage.getItem("VEHICLETYPELOOKUP") as string),
                statusMessage: "",
                messageCode: 200,
                statusCode: "Y"
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch vehicle type lookups";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetVehicleTypeLookUp',
                severity: ErrorSeverity.Error
            });
            return {
                result: [] as Array<VehicleTypeLookUp>,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    async GetDocumentTypeLookUp(): Promise<ApiResponse<Array<DocumentTypeLookUp>>> {
        try {
            if (!sessionStorage.getItem("DOCUMENTTYPELOOKUP")) {
                let res = (await this.GetAllLookUps()).result;

                if (!res) {
                    return {
                        result: [] as Array<DocumentTypeLookUp>,
                        statusMessage: "Failed to load document type lookups",
                        messageCode: 500,
                        statusCode: "N"
                    };
                }

                let _documentTypes = res.find(x => x.key === "DOCUMENT_TYPE")?.value;

                let documentTypes = [] as DocumentTypeLookUp[];

                if (_documentTypes !== null && _documentTypes !== undefined) {
                    for (const documentType of _documentTypes) {
                        if (documentType.isActive === "Y")
                            documentTypes.push({ DocumentTypeCode: documentType.code, DocumentTypeName: documentType.description } as DocumentTypeLookUp);
                    }
                }

                sessionStorage.setItem("DOCUMENTTYPELOOKUP", JSON.stringify(documentTypes));
            }

            return {
                result: JSON.parse(sessionStorage.getItem("DOCUMENTTYPELOOKUP") as string),
                statusMessage: "",
                messageCode: 200,
                statusCode: "Y"
            };
        } catch (error: any) {
            const message = error?.response?.data?.statusMessage || error?.message || "Failed to fetch document type lookups";
            ErrorLogger.get().logError(error, {
                component: 'TesterService',
                action: 'GetDocumentTypeLookUp',
                severity: ErrorSeverity.Error
            });
            return {
                result: [] as Array<DocumentTypeLookUp>,
                statusMessage: message,
                messageCode: error?.response?.status || 500,
                statusCode: "N"
            };
        }
    }

    formatGetVehiclesByTesterId(vehicles: TesterVehicle[]) {
        for (const vehicle of vehicles) {
            vehicle.DocumentsUploaded = [];

            const vehicleDocsGroup = vehicle.vehicleDocuments?.find(x => (x.documentGroupId ?? 0) > 0);
            // Find reg doc: prefer entry with no group (null/0/undefined), fallback to REG documentTypeId
            const vehicleRegistrationDoc = vehicle.vehicleDocuments?.find(x => !x.documentGroupId)
                ?? vehicle.vehicleDocuments?.find(x => x.documentTypeId === "REG");

            // Always set documentGroupId when a group link exists
            if (vehicleDocsGroup) {
                vehicle.documentGroupId = vehicleDocsGroup.documentGroupId ?? 0;
            }

            const hasRegDoc = vehicleRegistrationDoc
                || vehicle.vehicleDocuments?.some(x => x.documentTypeId === "REG");
            hasRegDoc ? vehicle.DocumentsUploaded.push("R") : vehicle.DocumentsUploaded.push("1");

            if (vehicleDocsGroup && (vehicleDocsGroup.documentGroupDetails?.documents?.length ?? 0) > 0) {
                vehicle.documentId = vehicleRegistrationDoc?.documentId ?? 0;
                this.setDocumentsUploaded(vehicleDocsGroup, vehicle);
            } else {
                vehicle.DocumentsUploaded.push("2", "3", "4", "5", "6");
            }
        }
        return vehicles;
    }

    private setDocumentsUploaded(vehicleDocsGroup: Document, vehicle: TesterVehicle) {
        let docs = vehicleDocsGroup?.documentGroupDetails?.documents ?? [];

        (docs.includes("SECURE")) ? vehicle.DocumentsUploaded.push("S") : vehicle.DocumentsUploaded.push("2");
        (docs.includes("EXTRICATION")) ? vehicle.DocumentsUploaded.push("E") : vehicle.DocumentsUploaded.push("3");
        (docs.includes("TOWING")) ? vehicle.DocumentsUploaded.push("T") : vehicle.DocumentsUploaded.push("4");
        (docs.includes("FIREFIGHTING")) ? vehicle.DocumentsUploaded.push("F") : vehicle.DocumentsUploaded.push("5");
        (docs.includes("POSTINCIDENT")) ? vehicle.DocumentsUploaded.push("I") : vehicle.DocumentsUploaded.push("6");
    }
}